import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const PAGE = 30;

const endpointFor = (target) =>
  target?.type === "comment"
    ? `/chatter/comments/${target.id}/reactions`
    : `/chatter/posts/${target?.id}/reactions`;

/**
 * Reactor population for one Chatter target, loaded on demand and paginated.
 * `emoji` null = the All view. Nothing is fetched until `enabled` is true, so normal feed
 * rendering never pays for reactor hydration.
 */
export function useReactors(target, emoji, enabled, limit = PAGE) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const offset = useRef(0);
  const seen = useRef(new Set());

  const load = useCallback(async (reset) => {
    if (!target?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(endpointFor(target), {
        params: { emoji: emoji || undefined, offset: reset ? 0 : offset.current, limit },
      });
      const rows = data.items || [];
      if (reset) {
        seen.current = new Set(rows.map((r) => `${r.user_id}:${r.emoji}`));
        setItems(rows);
        offset.current = rows.length;
      } else {
        const fresh = rows.filter((r) => !seen.current.has(`${r.user_id}:${r.emoji}`));
        fresh.forEach((r) => seen.current.add(`${r.user_id}:${r.emoji}`));
        setItems((s) => [...s, ...fresh]);
        offset.current += rows.length;
      }
      setSummary(data.summary || {});
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [target?.id, target?.type, emoji, limit]);

  useEffect(() => {
    if (!enabled) return;
    offset.current = 0;
    load(true);
  }, [enabled, load]);

  return {
    items, summary, total, hasMore, loading,
    loadMore: () => load(false),
    reload: () => load(true),
  };
}

export default useReactors;
